import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

/**
 * EventPromoModal — splash-page popup that promotes upcoming events.
 *
 * Fetches the public /event-promo endpoint on mount. Renders nothing
 * unless the admin has enabled it AND the event date hasn't passed yet.
 *
 * Show-once behavior: once a visitor closes the popup, we remember the
 * (flyer + event_date) signature in localStorage. The popup won't show
 * again on this device until the admin uploads a new flyer or changes
 * the event date — at which point all returning visitors see the fresh
 * promo automatically.
 */
const SEEN_STORAGE_KEY = "ll_event_promo_seen";

function promoSignature(p) {
  // Stable key derived from the content the visitor actually saw.
  return `${p.flyer_url || ""}::${p.event_date || ""}`;
}

function alreadySeen(p) {
  try {
    return window.localStorage.getItem(SEEN_STORAGE_KEY) === promoSignature(p);
  } catch {
    return false;
  }
}

function markSeen(p) {
  try {
    window.localStorage.setItem(SEEN_STORAGE_KEY, promoSignature(p));
  } catch { /* private mode / quota — fail silent */ }
}

export default function EventPromoModal() {
  const [promo, setPromo] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/event-promo").then(({ data }) => {
      if (cancelled) return;
      if (!data || !data.active) return;
      // Skip if this visitor already dismissed this exact promo.
      if (alreadySeen(data)) return;
      setPromo(data);
    }).catch(() => { /* silent — popup is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  const close = () => {
    if (promo) markSeen(promo);
    setClosed(true);
  };

  if (!promo || closed) return null;

  const ctaTarget = promo.cta_url || "";
  const ctaIsExternal = /^https?:\/\//i.test(ctaTarget);

  return (
    <div
      className="event-promo-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Upcoming event"
      onClick={close}
      data-testid="event-promo-modal"
    >
      <div
        className="event-promo-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="event-promo-close"
          onClick={close}
          aria-label="Close"
          data-testid="event-promo-close"
        >
          <X size={20} />
        </button>

        {promo.flyer_url && (
          <img
            src={promo.flyer_url}
            alt={promo.headline || "Upcoming event"}
            className="event-promo-flyer"
            data-testid="event-promo-flyer"
          />
        )}

        {(promo.headline || promo.subheadline) && (
          <div className="event-promo-text">
            {promo.headline && (
              <div
                className="event-promo-headline"
                data-testid="event-promo-headline"
              >
                {promo.headline}
              </div>
            )}
            {promo.subheadline && (
              <div
                className="event-promo-subheadline"
                data-testid="event-promo-subheadline"
              >
                {promo.subheadline}
              </div>
            )}
          </div>
        )}

        {promo.cta_label && ctaTarget && (
          <a
            href={ctaTarget}
            target={ctaIsExternal ? "_blank" : undefined}
            rel={ctaIsExternal ? "noopener noreferrer" : undefined}
            className="event-promo-cta"
            data-testid="event-promo-cta"
            onClick={() => promo && markSeen(promo)}
          >
            {promo.cta_label}
          </a>
        )}

        <button
          type="button"
          onClick={close}
          className="event-promo-close-btn"
          data-testid="event-promo-close-btn"
        >
          Close
        </button>
      </div>
    </div>
  );
}
